/*
 * Agency mode's screens (draft 13, FEATURE-MAP 14), for The Marketers only: Home (monthly recurring revenue, deals by
 * stage, tasks due, unpaid invoices, rank moves), Deals (the pipeline with call slides and proposals), Tasks, Projects,
 * Proposals and signed plans, the Clients table, a client's page, and Results (a dashboard per client).
 * Owner rules: each page says what it is for, plain words for staff who speak English as a second language, a time zone
 * on every time, and no tips, crew or visits.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import {
  HEALTH_WORDS,
  LIFECYCLE_WORDS,
  LOCAL_PACKAGE_LINES,
  PROJECT_STATUS_WORDS,
  PROJECT_STATUSES,
  TASK_KINDS,
  zoneShort,
  type AgencyClientRow,
  type AgreementRow,
  type ClientResults,
  type DeckRow,
  type Health,
  type LeadRow,
  type ProjectRow,
  type ProjectStatus,
  type TaskKind,
  type TaskRow,
  type TASK_VIEWS,
} from "@steward/shared";
import { addDays } from "@steward/shared";
import {
  useAddProject,
  useAddReport,
  useAddTask,
  useAgencyClient,
  useAgencyClients,
  useAgencyHome,
  useChangeProject,
  useChangeTask,
  useClientLink,
  useClientOwner,
  useClientResults,
  useDecks,
  useMakeSlides,
  useNotice,
  usePlans,
  useProjects,
  useProposalFromDeal,
  useResults,
  useTasks,
  useTeam,
} from "../api/agency";
import { useCreateLead, useMoveLead, usePipeline, useQuotes } from "../api/money";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Choice, FormField, InputText } from "../components/fields";
import { money, plural, shortDate, todayIn } from "../components/format";
import { Icon } from "../components/Icon";
import { Menu, Modal, useToast } from "../components/overlays";
import {
  ago,
  Avatar,
  Banner,
  Button,
  Card,
  Details,
  EmptyState,
  IconButton,
  LoadError,
  Page,
  Skeleton,
  StatusLabel,
  type Status,
} from "../components/parts";
import { errText } from "../components/work";

const HEALTH_STATUS: Record<Health, Status> = { good: "success", watch: "warning", bad: "critical", none: "inactive" };
const KIND_WORDS: Record<TaskKind, string> = { todo: "To do", call: "Call", email: "Email", meeting: "Meeting" };
const PROJECT_STATUS: Record<ProjectStatus, Status> = {
  onboarding: "informative",
  active: "success",
  paused: "warning",
  done: "inactive",
  stopped: "inactive",
};

export function HealthPill({ health }: { health: Health }) {
  return (
    <StatusLabel status={HEALTH_STATUS[health]} pill>
      {HEALTH_WORDS[health]}
    </StatusLabel>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

/** Rank move words: a lower place is better. */
function rankMove(now: number | null, before: number | null): { text: string; cls: string } {
  if (now === null) return { text: "No grid yet", cls: "" };
  if (before === null) return { text: `Place ${now.toFixed(1)}`, cls: "" };
  const d = before - now;
  if (Math.abs(d) < 0.05) return { text: `Place ${now.toFixed(1)}, same`, cls: "" };
  return { text: `Place ${now.toFixed(1)}, ${d > 0 ? "up" : "down"} ${Math.abs(d).toFixed(1)}`, cls: d > 0 ? "up" : "down" };
}

/** The day an instant falls on in the company's time zone. */
const localDay = (iso: string, tz: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: tz });

function leadsMove(now: number | null, before: number | null, months = false): string {
  if (now === null) return "No numbers";
  if (before === null) return `${String(now)} leads`;
  return `${String(now)} leads, ${String(before)} ${months ? "the month before" : "same days last month"}`;
}

// Home -----------------------------------------------------------------------------------------------------------------

export function AgencyHome() {
  const { me, company } = useSession();
  const q = useAgencyHome(company.id);
  const first = me.name.split(" ")[0] ?? me.name;
  const zone = zoneShort(company.timezone);
  return (
    <Page
      title={`Hello, ${first}`}
      subtitle={`${company.name} · times in ${zone}`}
      intro="Your agency at a glance. Money that comes in each month, deals in progress, what is due today, and how clients are doing."
    >
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !q.data ? (
        <Skeleton rows={6} />
      ) : (
        <div className="stack">
          <div className="tiles">
            <Tile label="Monthly plans" value={money(q.data.mrrCents)} hint={`${plural(q.data.livePlans, "plan")} running`} />
            <Tile
              label="Stopping soon"
              value={String(q.data.onNotice)}
              hint={q.data.onNotice ? "Plans with notice given" : "No one has asked to stop"}
            />
            <Tile label="Unpaid invoices" value={money(q.data.unpaid.cents)} hint={plural(q.data.unpaid.count, "invoice")} />
            <Tile label="Late tasks" value={String(q.data.overdue)} hint={q.data.overdue ? "Past their day" : "All on time"} />
          </div>
          <Card title="Deals by stage" action={<Link to="/deals">Open deals</Link>}>
            <div className="ag-stages">
              {q.data.stages.map((s) => (
                <div key={s.stageId} className="ag-stage">
                  <span className="muted small">{s.name}</span>
                  <b className="num">{money(s.valueCents)}</b>
                  <span className="muted small">{plural(s.count, "deal")}</span>
                </div>
              ))}
            </div>
          </Card>
          <div className="grid-2">
            <Card title="Due today and late" action={<Link to="/tasks">All tasks</Link>}>
              <TaskList rows={q.data.tasksDue} empty="Nothing due today." />
            </Card>
            <Card title="Map rank moves" action={<Link to="/results">All results</Link>}>
              {q.data.rankMoves.length ? (
                <ul className="ag-list">
                  {q.data.rankMoves.map((r) => {
                    const m = rankMove(r.rankNow, r.rankBefore);
                    return (
                      <li key={r.clientId}>
                        <Link to="/clients/$id" params={{ id: r.clientId }}>
                          {r.name}
                        </Link>
                        <span className={`small ${m.cls}`}>{m.text}</span>
                        <HealthPill health={r.health} />
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="muted">No client has two map rank checks yet.</p>
              )}
            </Card>
          </div>
        </div>
      )}
    </Page>
  );
}

// Tasks ----------------------------------------------------------------------------------------------------------------

/** Which date heading a task sits under, so a long list scans by when (Late, Today, Tomorrow, This week, Later). */
function dueGroup(t: TaskRow, today: string): string {
  if (t.overdue || t.dueOn < today) return "Late";
  if (t.dueOn === today) return "Today";
  if (t.dueOn === addDays(today, 1)) return "Tomorrow";
  if (t.dueOn <= addDays(today, 6)) return "This week";
  return "Later";
}

function TaskList({ rows, empty, today }: { rows: TaskRow[]; empty: string; today?: string }) {
  if (!rows.length) return <p className="muted">{empty}</p>;
  if (!today || rows.some((t) => t.doneAt)) return <TaskItems rows={rows} />;
  const groups = new Map<string, TaskRow[]>();
  for (const t of rows) groups.set(dueGroup(t, today), [...(groups.get(dueGroup(t, today)) ?? []), t]);
  return (
    <>
      {[...groups].map(([name, list]) => (
        <section key={name} className="ag-group">
          <h3 className={name === "Late" ? "late" : undefined}>
            {name} <span className="muted num">{list.length}</span>
          </h3>
          <TaskItems rows={list} />
        </section>
      ))}
    </>
  );
}

function TaskItems({ rows }: { rows: TaskRow[] }) {
  const change = useChangeTask();
  const toast = useToast();
  return (
    <ul className="ag-tasks">
      {rows.map((t) => (
        <li key={t.id} className={t.doneAt ? "done" : t.overdue ? "late" : undefined}>
          <input
            type="checkbox"
            aria-label={`Done: ${t.title}`}
            checked={Boolean(t.doneAt)}
            onChange={(e) =>
              change.mutate(
                { id: t.id, done: e.target.checked },
                {
                  onSuccess: () =>
                    toast(e.target.checked ? "Task done" : "Task open again", {
                      undo: () => change.mutate({ id: t.id, done: !e.target.checked }),
                    }),
                  onError: (err) => toast(errText(err), { kind: "error" }),
                },
              )
            }
          />
          <div>
            <b>{t.title}</b>
            <span className="muted small">
              {KIND_WORDS[t.kind]} · {t.overdue ? "Late, was due " : "Due "}
              {t.dueWords}
            </span>
            {t.clientName || t.projectName || t.leadName ? (
              <span className="muted small">
                {[t.clientName, t.projectName, t.leadName && !t.clientName ? t.leadName : null].filter(Boolean).join(" · ")}
              </span>
            ) : null}
          </div>
          {t.assignedName ? (
            <span className="ag-who" data-tip={t.assignedName}>
              <Avatar name={t.assignedName} />
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

const VIEW_WORDS: Record<(typeof TASK_VIEWS)[number], string> = {
  open: "Open",
  mine: "Mine",
  today: "Due today",
  overdue: "Late",
  done: "Done",
};

export function Tasks() {
  const { company } = useSession();
  const [view, setView] = useState<(typeof TASK_VIEWS)[number]>("open");
  const [adding, setAdding] = useState(false);
  const q = useTasks(company.id, view);
  return (
    <Page
      title="Tasks"
      subtitle={`Times in ${zoneShort(company.timezone)}`}
      intro="Everything the team needs to do for clients and deals. Tick a task when it is done."
      primaryAction={
        <Button icon="plus" onClick={() => setAdding(true)}>
          New task
        </Button>
      }
    >
      <div className="segmented page-tabs" role="tablist" aria-label="Tasks">
        {(Object.keys(VIEW_WORDS) as (typeof TASK_VIEWS)[number][]).map((v) => (
          <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)}>
            {VIEW_WORDS[v]}
          </button>
        ))}
      </div>
      <Card>
        {q.error ? (
          <LoadError error={q.error} retry={() => void q.refetch()} />
        ) : q.data ? (
          <TaskList
            rows={q.data}
            empty={view === "done" ? "No finished tasks yet." : "No tasks here."}
            today={todayIn(company.timezone)}
          />
        ) : (
          <Skeleton rows={5} />
        )}
      </Card>
      <TaskForm open={adding} onClose={() => setAdding(false)} />
    </Page>
  );
}

function TaskForm({
  open,
  onClose,
  clientId,
  leadId,
}: {
  open: boolean;
  onClose: () => void;
  clientId?: string;
  leadId?: string;
}) {
  const { company, me } = useSession();
  const team = useTeam(company.id);
  const clients = useAgencyClients(company.id);
  const add = useAddTask();
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<TaskKind>("todo");
  const [dueOn, setDueOn] = useState(todayIn(company.timezone));
  const [dueTime, setDueTime] = useState("");
  const [who, setWho] = useState(me.id);
  const [client, setClient] = useState(clientId ?? "");
  const save = () =>
    add.mutate(
      {
        title,
        kind,
        dueOn,
        dueTime: dueTime || null,
        assignedTo: who || null,
        clientId: client || null,
        leadId: leadId ?? null,
      },
      {
        onSuccess: () => {
          toast("Task added");
          setTitle("");
          onClose();
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Modal
      title="New task"
      open={open}
      onClose={onClose}
      footer={
        <Button loading={add.isPending} disabled={!title.trim()} onClick={save}>
          Add task
        </Button>
      }
    >
      <div className="form-grid">
        <InputText label="What needs doing" value={title} onChange={(e) => setTitle(e.target.value)} wide />
        <Choice
          label="Kind"
          value={kind}
          onChange={setKind}
          options={TASK_KINDS.map((k) => ({ value: k, label: KIND_WORDS[k] }))}
        />
        <InputText label="Due day" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
        <InputText
          label="Time"
          type="time"
          value={dueTime}
          onChange={(e) => setDueTime(e.target.value)}
          description={`Leave empty for any time. Times are ${zoneShort(company.timezone)}.`}
        />
        <FormField label="For">
          <select className="select" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">Nobody yet</option>
            {(team.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </FormField>
        {clientId ? null : (
          <FormField label="Client">
            <select className="select" value={client} onChange={(e) => setClient(e.target.value)}>
              <option value="">No client</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FormField>
        )}
      </div>
    </Modal>
  );
}

// Deals ----------------------------------------------------------------------------------------------------------------

export function Deals() {
  const { company, can } = useSession();
  const pipe = usePipeline(company.id);
  const move = useMoveLead();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [slidesFor, setSlidesFor] = useState<LeadRow | null>(null);
  const [proposalFor, setProposalFor] = useState<LeadRow | null>(null);
  const editable = can("lead.update");
  const p = pipe.data;
  return (
    <Page
      title="Deals"
      intro="Businesses we are talking to, from first contact to signed. Website leads land in New lead. Make call slides before a call, then a proposal after it."
      primaryAction={
        editable ? (
          <Button icon="plus" onClick={() => setAdding(true)}>
            New deal
          </Button>
        ) : null
      }
    >
      {pipe.error ? (
        <LoadError error={pipe.error} retry={() => void pipe.refetch()} />
      ) : !p ? (
        <Skeleton rows={6} />
      ) : (
        <div className="pipeline">
          {p.stages.map((s) => {
            const here = p.leads.filter((l) => l.stageId === s.id);
            return (
              <section className="stage" key={s.id}>
                <div className="stage-head">
                  <b>{s.name}</b>
                  <span className="muted num">
                    {here.length} · {money(here.reduce((t, l) => t + (l.valueCents ?? 0), 0))}
                  </span>
                </div>
                {here.length ? (
                  here.map((l) => (
                    <div className="lead" key={l.id}>
                      <div className="row between">
                        <b>{l.businessName ?? l.name}</b>
                        {editable ? (
                          <Menu
                            align="right"
                            button={({ toggle }) => (
                              <IconButton icon="more" label={`More for ${l.businessName ?? l.name}`} size={18} onClick={toggle} />
                            )}
                          >
                            {(close) => (
                              <>
                                {p.stages
                                  .filter((x) => x.id !== l.stageId)
                                  .map((x) => (
                                    <button
                                      key={x.id}
                                      type="button"
                                      role="menuitem"
                                      onClick={() => {
                                        close();
                                        move.mutate(
                                          { id: l.id, stageId: x.id },
                                          {
                                            onSuccess: () => toast(`Moved to ${x.name}`),
                                            onError: (e) => toast(errText(e), { kind: "error" }),
                                          },
                                        );
                                      }}
                                    >
                                      Move to {x.name}
                                    </button>
                                  ))}
                              </>
                            )}
                          </Menu>
                        ) : null}
                      </div>
                      {l.businessName ? <span className="muted small">{l.name}</span> : null}
                      <span className="muted small">
                        {l.service ?? "No service yet"}
                        {l.valueCents ? ` · ${money(l.valueCents)} a month` : ""}
                      </span>
                      <span className="muted small">
                        {l.source ? `${l.source} · ` : ""}
                        {ago(l.stageChangedAt)}
                      </span>
                      {editable && !s.fixed ? (
                        <div className="row">
                          <Button type="tertiary" size="small" icon="file" onClick={() => setSlidesFor(l)}>
                            Call slides
                          </Button>
                          {l.quoteId ? (
                            <Link to="/quotes/$id" params={{ id: l.quoteId }} className="btn tertiary small">
                              Proposal
                            </Link>
                          ) : (
                            <Button type="tertiary" size="small" icon="quotes" onClick={() => setProposalFor(l)}>
                              Proposal
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <p className="muted small stage-empty">Nobody here</p>
                )}
              </section>
            );
          })}
        </div>
      )}
      <DealForm open={adding} onClose={() => setAdding(false)} />
      {slidesFor ? <SlidesPanel lead={slidesFor} onClose={() => setSlidesFor(null)} /> : null}
      {proposalFor ? <ProposalForm lead={proposalFor} onClose={() => setProposalFor(null)} /> : null}
    </Page>
  );
}

function DealForm({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateLead();
  const toast = useToast();
  const [f, setF] = useState({
    businessName: "",
    name: "",
    phone: "",
    email: "",
    website: "",
    service: "",
    address: "",
    value: "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = () =>
    create.mutate(
      {
        name: f.name || f.businessName,
        businessName: f.businessName || null,
        phone: f.phone || null,
        email: f.email || null,
        website: f.website || null,
        service: f.service || null,
        address: f.address || null,
        valueCents: f.value ? Math.round(Number(f.value) * 100) : null,
        source: "Added by hand",
      },
      {
        onSuccess: () => {
          toast("Deal added");
          onClose();
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Modal
      title="New deal"
      open={open}
      onClose={onClose}
      footer={
        <Button loading={create.isPending} disabled={!f.businessName.trim() && !f.name.trim()} onClick={save}>
          Add deal
        </Button>
      }
    >
      <div className="form-grid">
        <InputText label="Business" value={f.businessName} onChange={set("businessName")} />
        <InputText label="Contact person" value={f.name} onChange={set("name")} />
        <InputText label="Phone" value={f.phone} onChange={set("phone")} />
        <InputText label="Email" type="email" value={f.email} onChange={set("email")} />
        <InputText label="Website" value={f.website} onChange={set("website")} />
        <InputText label="What they do" value={f.service} onChange={set("service")} description="Like plumbing or roofing" />
        <InputText label="Address or city" value={f.address} onChange={set("address")} wide />
        <InputText
          label="Plan size a month"
          inputMode="decimal"
          prefix="$"
          value={f.value}
          onChange={set("value")}
          description="A guess is fine. It adds up on Home."
        />
      </div>
    </Modal>
  );
}

/** Make and show sales call slides. Looking up nearby businesses costs money, so it is a tick box, off to start. */
function SlidesPanel({ lead, onClose }: { lead: LeadRow; onClose: () => void }) {
  const { company } = useSession();
  const decks = useDecks(lead.id);
  const make = useMakeSlides();
  const toast = useToast();
  const [paid, setPaid] = useState(false);
  const [show, setShow] = useState<DeckRow | null>(null);
  if (show) return <SlideShow deck={show} onClose={() => setShow(null)} />;
  return (
    <Modal
      title={`Call slides for ${lead.businessName ?? lead.name}`}
      open
      onClose={onClose}
      footer={
        <Button
          loading={make.isPending}
          onClick={() =>
            make.mutate(
              { leadId: lead.id, lookUpCompetitors: paid },
              { onSuccess: (d) => setShow(d), onError: (e) => toast(errText(e), { kind: "error" }) },
            )
          }
        >
          Make slides
        </Button>
      }
    >
      <p className="muted">
        8 to 10 slides from what we can check. Their website and Google profile are read. Nothing is made up. A slide we could not
        check says so.
      </p>
      <label className="ag-check">
        <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} />
        <span>
          Look up nearby businesses on Google
          <span className="muted small"> It costs a little money each time.</span>
        </span>
      </label>
      {decks.data?.length ? (
        <>
          <h3>Made before</h3>
          <ul className="ag-list">
            {decks.data.map((d) => (
              <li key={d.id}>
                <button type="button" className="linkish" onClick={() => setShow(d)}>
                  {shortDate(localDay(d.createdAt, company.timezone))}, {plural(d.slides.length, "slide")}
                </button>
                {d.paidChecks ? <span className="muted small">with nearby businesses</span> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Modal>
  );
}

function SlideShow({ deck, onClose }: { deck: DeckRow; onClose: () => void }) {
  const [i, setI] = useState(0);
  const s = deck.slides[i]!;
  const last = deck.slides.length - 1;
  return (
    <div
      className="ag-show"
      role="dialog"
      aria-label="Call slides"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") setI(Math.min(last, i + 1));
        if (e.key === "ArrowLeft") setI(Math.max(0, i - 1));
        if (e.key === "Escape") onClose();
      }}
      ref={(el) => el?.focus()}
    >
      <div className="ag-show-bar">
        <span className="muted num">
          {i + 1} of {deck.slides.length}
        </span>
        <a className="btn secondary small" href={`/v1/agency/slides/${deck.id}/pdf`} target="_blank" rel="noreferrer">
          PDF
        </a>
        <IconButton icon="close" label="Close slides" onClick={onClose} />
      </div>
      <div className="ag-slide">
        <h2>{s.title}</h2>
        <ul>
          {s.lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      </div>
      <div className="ag-show-nav">
        <Button type="secondary" icon="chevronLeft" disabled={i === 0} onClick={() => setI(i - 1)}>
          Back
        </Button>
        <Button disabled={i === last} onClick={() => setI(i + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

function ProposalForm({ lead, onClose }: { lead: LeadRow; onClose: () => void }) {
  const make = useProposalFromDeal();
  const navigate = useNavigate();
  const toast = useToast();
  const [monthly, setMonthly] = useState(lead.valueCents ? String(lead.valueCents / 100) : "");
  const [setup, setSetup] = useState("");
  const [what, setWhat] = useState("Monthly marketing");
  // The local package's own monthly lines (answer 116): ticked ones need a price.
  const [extras, setExtras] = useState<Record<string, string | null>>({});
  const cents = (v: string) => Math.round(Number(v || "0") * 100);
  const picked = Object.entries(extras).filter((e): e is [string, string] => e[1] !== null);
  return (
    <Modal
      title={`Proposal for ${lead.businessName ?? lead.name}`}
      open
      onClose={onClose}
      footer={
        <Button
          loading={make.isPending}
          disabled={cents(monthly) < 100 || picked.some(([, v]) => cents(v) < 100)}
          onClick={() =>
            make.mutate(
              {
                leadId: lead.id,
                monthlyCents: cents(monthly),
                setupCents: cents(setup),
                description: what,
                extras: picked.map(([description, v]) => ({ description, monthlyCents: cents(v) })),
              },
              {
                onSuccess: (r) => void navigate({ to: "/quotes/$id", params: { id: r.quoteId } }),
                onError: (e) => toast(errText(e), { kind: "error" }),
              },
            )
          }
        >
          Make the proposal
        </Button>
      }
    >
      <p className="muted">
        A 3 month plan. They pay month 1 and the setup when they sign. Monthly bills start a month later. After 3 months it is
        month to month with 30 days notice. Ad spend is paid to Meta or Google directly.
      </p>
      <div className="form-grid">
        <InputText label="What it is" value={what} onChange={(e) => setWhat(e.target.value)} wide />
        <InputText
          label="Each month"
          prefix="$"
          inputMode="decimal"
          value={monthly}
          onChange={(e) => setMonthly(e.target.value)}
        />
        <InputText label="Setup, once" prefix="$" inputMode="decimal" value={setup} onChange={(e) => setSetup(e.target.value)} />
      </div>
      <h3>Local package</h3>
      {LOCAL_PACKAGE_LINES.map((line) => (
        <div key={line} className="form-grid">
          <label className="ag-check">
            <input
              type="checkbox"
              checked={extras[line] !== undefined && extras[line] !== null}
              onChange={(e) => setExtras({ ...extras, [line]: e.target.checked ? "" : null })}
            />
            <span>{line}, its own line each month</span>
          </label>
          {extras[line] !== undefined && extras[line] !== null ? (
            <InputText
              label={`${line} each month`}
              prefix="$"
              inputMode="decimal"
              value={extras[line]}
              onChange={(e) => setExtras({ ...extras, [line]: e.target.value })}
            />
          ) : null}
        </div>
      ))}
    </Modal>
  );
}

// Proposals and plans --------------------------------------------------------------------------------------------------

export function Proposals() {
  const { company, can } = useSession();
  const [tab, setTab] = useState<"proposals" | "plans">("proposals");
  const quotes = useQuotes(company.id, "all");
  const plans = usePlans(company.id, can("invoice.read"));
  const navigate = useNavigate();
  return (
    <Page
      title="Proposals"
      intro="Proposals sent to businesses, and the plans they signed. Make a proposal from a deal on the Deals page."
    >
      <div className="segmented page-tabs" role="tablist" aria-label="Proposals">
        <button type="button" role="tab" aria-selected={tab === "proposals"} onClick={() => setTab("proposals")}>
          Proposals
        </button>
        {can("invoice.read") ? (
          <button type="button" role="tab" aria-selected={tab === "plans"} onClick={() => setTab("plans")}>
            Signed plans
          </button>
        ) : null}
      </div>
      {tab === "proposals" ? (
        <DataTable
          title="Proposals"
          rows={quotes.data?.quotes ?? []}
          loading={quotes.isLoading}
          error={quotes.error ? <LoadError error={quotes.error} /> : undefined}
          rowKey={(r) => r.id}
          onRowClick={(r) => void navigate({ to: "/quotes/$id", params: { id: r.id } })}
          search={(r) => `${r.name} ${r.title ?? ""}`}
          empty={
            <EmptyState icon="info" title="No proposals yet">
              Open a deal and press Proposal.
            </EmptyState>
          }
          columns={[
            { key: "n", header: "#", render: (r) => <span className="num">{r.number}</span>, sort: (r) => r.number },
            { key: "who", header: "Business", render: (r) => r.name, sort: (r) => r.name },
            { key: "what", header: "Plan", render: (r) => r.title ?? "" },
            { key: "status", header: "Status", render: (r) => <ProposalStatus status={r.status} /> },
            {
              key: "sent",
              header: "Sent",
              render: (r) => (r.sentAt ? shortDate(localDay(r.sentAt, company.timezone)) : "Not sent"),
            },
            { key: "total", header: "Total", align: "right", render: (r) => money(r.totalCents), sort: (r) => r.totalCents },
          ]}
        />
      ) : (
        <PlansTable rows={plans.data ?? []} loading={plans.isLoading} />
      )}
    </Page>
  );
}

function ProposalStatus({ status }: { status: string }) {
  const map: Record<string, [Status, string]> = {
    draft: ["inactive", "Draft"],
    sent: ["informative", "Waiting"],
    approved: ["success", "Signed"],
    converted: ["success", "Signed"],
    declined: ["critical", "Said no"],
    expired: ["warning", "Ran out"],
  };
  const [s, w] = map[status] ?? ["inactive", status];
  return <StatusLabel status={s}>{w}</StatusLabel>;
}

const PLAN_STATE: Record<AgreementRow["state"], [Status, string]> = {
  active: ["success", "Running"],
  notice: ["warning", "Stopping"],
  ended: ["inactive", "Ended"],
};

function PlansTable({ rows, loading }: { rows: AgreementRow[]; loading: boolean }) {
  const [notice, setNotice] = useState<AgreementRow | null>(null);
  return (
    <>
      <DataTable
        title="Signed plans"
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        empty={
          <EmptyState icon="info" title="No signed plans yet">
            A plan starts when a business signs a proposal.
          </EmptyState>
        }
        columns={[
          {
            key: "who",
            header: "Client",
            render: (r) => (
              <Link to="/clients/$id" params={{ id: r.clientId }}>
                {r.clientName}
              </Link>
            ),
            sort: (r) => r.clientName,
          },
          { key: "m", header: "Each month", align: "right", render: (r) => money(r.monthlyCents), sort: (r) => r.monthlyCents },
          {
            key: "state",
            header: "Status",
            render: (r) => <StatusLabel status={PLAN_STATE[r.state][0]}>{PLAN_STATE[r.state][1]}</StatusLabel>,
          },
          { key: "start", header: "Signed", render: (r) => shortDate(r.startOn), sort: (r) => r.startOn },
          { key: "term", header: "3 months end", render: (r) => shortDate(r.termEndsOn) },
          { key: "next", header: "Next bill", render: (r) => (r.nextBillOn ? shortDate(r.nextBillOn) : "None") },
          { key: "auto", header: "Auto pay", render: (r) => (r.autoPay ? "Yes" : "No") },
          {
            key: "act",
            header: "",
            render: (r) =>
              r.state === "ended" ? null : (
                <Button type="tertiary" size="small" onClick={() => setNotice(r)}>
                  {r.state === "notice" ? "Notice" : "They want to stop"}
                </Button>
              ),
          },
        ]}
      />
      {notice ? <NoticeForm plan={notice} onClose={() => setNotice(null)} /> : null}
    </>
  );
}

function NoticeForm({ plan, onClose }: { plan: AgreementRow; onClose: () => void }) {
  const { company } = useSession();
  const save = useNotice();
  const toast = useToast();
  const [day, setDay] = useState(plan.noticeOn ?? todayIn(company.timezone));
  const done = (msg: string) => ({
    onSuccess: () => {
      toast(msg);
      onClose();
    },
    onError: (e: unknown) => toast(errText(e), { kind: "error" }),
  });
  return (
    <Modal
      title={`${plan.clientName} wants to stop`}
      open
      onClose={onClose}
      footer={
        <>
          {plan.noticeOn ? (
            <Button type="secondary" onClick={() => save.mutate({ id: plan.id, noticeOn: null }, done("Notice taken back"))}>
              They changed their mind
            </Button>
          ) : null}
          <Button loading={save.isPending} onClick={() => save.mutate({ id: plan.id, noticeOn: day }, done("Notice saved"))}>
            Save notice
          </Button>
        </>
      }
    >
      <p className="muted">
        The plan runs at least to {shortDate(plan.termEndsOn)}. After that it ends 30 days after they told us. Months that start
        before the last day are billed.
      </p>
      {plan.leftIfStoppedCents > 0 ? (
        <Banner type="warning">{money(plan.leftIfStoppedCents)} is left in the first 3 months.</Banner>
      ) : null}
      <InputText label="The day they told us" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
    </Modal>
  );
}

// Projects -------------------------------------------------------------------------------------------------------------

export function Projects() {
  const { company } = useSession();
  const q = useProjects(company.id);
  const change = useChangeProject();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  return (
    <Page
      title="Projects"
      intro="The work we do for each client. A monthly plan becomes a project when it is signed, with its setup tasks."
      primaryAction={
        <Button icon="plus" onClick={() => setAdding(true)}>
          New project
        </Button>
      }
    >
      <DataTable<ProjectRow>
        title="Projects"
        rows={q.data ?? []}
        loading={q.isLoading}
        error={q.error ? <LoadError error={q.error} /> : undefined}
        rowKey={(r) => r.id}
        search={(r) => `${r.name} ${r.clientName}`}
        empty={
          <EmptyState icon="info" title="No projects yet">
            Projects start when a plan is signed, or add one here.
          </EmptyState>
        }
        columns={[
          { key: "n", header: "#", render: (r) => <span className="num">{r.number}</span>, sort: (r) => r.number },
          { key: "name", header: "Project", render: (r) => r.name, sort: (r) => r.name },
          {
            key: "client",
            header: "Client",
            render: (r) => (
              <Link to="/clients/$id" params={{ id: r.clientId }}>
                {r.clientName}
              </Link>
            ),
            sort: (r) => r.clientName,
          },
          { key: "kind", header: "Kind", render: (r) => (r.kind === "retainer" ? "Monthly plan" : "One project") },
          {
            key: "status",
            header: "Status",
            render: (r) => (
              <Menu
                button={({ toggle }) => (
                  <button type="button" className="linkish" onClick={toggle}>
                    <StatusLabel status={PROJECT_STATUS[r.status]}>{PROJECT_STATUS_WORDS[r.status]}</StatusLabel>
                  </button>
                )}
              >
                {(close) =>
                  PROJECT_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        close();
                        change.mutate({ id: r.id, status: s }, { onError: (e) => toast(errText(e), { kind: "error" }) });
                      }}
                    >
                      {PROJECT_STATUS_WORDS[s]}
                    </button>
                  ))
                }
              </Menu>
            ),
          },
          { key: "tasks", header: "Tasks", render: (r) => `${String(r.doneTasks)} of ${String(r.openTasks + r.doneTasks)} done` },
          { key: "m", header: "Each month", align: "right", render: (r) => (r.monthlyCents ? money(r.monthlyCents) : "") },
        ]}
      />
      <ProjectForm open={adding} onClose={() => setAdding(false)} />
    </Page>
  );
}

function ProjectForm({ open, onClose, clientId }: { open: boolean; onClose: () => void; clientId?: string }) {
  const { company } = useSession();
  const clients = useAgencyClients(company.id);
  const add = useAddProject();
  const toast = useToast();
  const [name, setName] = useState("");
  const [client, setClient] = useState(clientId ?? "");
  const [due, setDue] = useState("");
  return (
    <Modal
      title="New project"
      open={open}
      onClose={onClose}
      footer={
        <Button
          loading={add.isPending}
          disabled={!name.trim() || !client}
          onClick={() =>
            add.mutate(
              { clientId: client, name, kind: "project", dueOn: due || null },
              {
                onSuccess: () => {
                  toast("Project added");
                  onClose();
                },
                onError: (e) => toast(errText(e), { kind: "error" }),
              },
            )
          }
        >
          Add project
        </Button>
      }
    >
      <div className="form-grid">
        <InputText label="Project" value={name} onChange={(e) => setName(e.target.value)} wide description="Like New website" />
        {clientId ? null : (
          <FormField label="Client">
            <select className="select" value={client} onChange={(e) => setClient(e.target.value)}>
              <option value="">Pick a client</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FormField>
        )}
        <InputText label="Due day" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
      </div>
    </Modal>
  );
}

// Clients --------------------------------------------------------------------------------------------------------------

export function AgencyClients() {
  const { company } = useSession();
  const q = useAgencyClients(company.id);
  const navigate = useNavigate();
  return (
    <Page
      title="Clients"
      intro="Every business we work with or talk to. Click a row to see its plan, tasks and results."
      primaryAction={
        <Button icon="plus" onClick={() => void navigate({ to: "/clients/new" })}>
          New client
        </Button>
      }
    >
      <DataTable<AgencyClientRow>
        title="Clients"
        rows={q.data ?? []}
        loading={q.isLoading}
        error={q.error ? <LoadError error={q.error} /> : undefined}
        rowKey={(r) => r.id}
        onRowClick={(r) => void navigate({ to: "/clients/$id", params: { id: r.id } })}
        search={(r) => `${r.name} ${r.contact ?? ""} ${r.email ?? ""}`}
        empty={
          <EmptyState icon="info" title="No clients yet">
            A client is added when a deal signs, or add one here.
          </EmptyState>
        }
        columns={[
          { key: "name", header: "Business", render: (r) => <b>{r.name}</b>, sort: (r) => r.name },
          { key: "contact", header: "Contact", render: (r) => r.contact ?? "" },
          { key: "owner", header: "Looked after by", render: (r) => r.ownerName ?? "Nobody yet", sort: (r) => r.ownerName ?? "" },
          {
            key: "plan",
            header: "Each month",
            align: "right",
            render: (r) => (r.retainerCents ? money(r.retainerCents) : ""),
            sort: (r) => r.retainerCents,
          },
          { key: "stage", header: "Stage", render: (r) => LIFECYCLE_WORDS[r.lifecycle], sort: (r) => r.lifecycle },
          { key: "health", header: "Results", render: (r) => <HealthPill health={r.health} />, sort: (r) => r.health },
          {
            key: "last",
            header: "Last activity",
            render: (r) => (r.lastActivityAt ? ago(r.lastActivityAt) : ""),
            sort: (r) => r.lastActivityAt ?? "",
          },
          { key: "tasks", header: "Open tasks", align: "right", render: (r) => String(r.openTasks), sort: (r) => r.openTasks },
        ]}
      />
    </Page>
  );
}

export function AgencyClient({ id }: { id: string }) {
  const { company, can } = useSession();
  const q = useAgencyClient(id);
  const team = useTeam(company.id);
  const owner = useClientOwner();
  const link = useClientLink();
  const toast = useToast();
  const [adding, setAdding] = useState<"task" | "project" | "report" | null>(null);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={8} />;
  const d = q.data;
  const c = d.client;
  return (
    <Page
      title={c.name}
      titleMetaData={<HealthPill health={c.health} />}
      subtitle={`${LIFECYCLE_WORDS[c.lifecycle]}${c.contact ? ` · ${c.contact}` : ""}`}
      intro="One client in one place. Their plan, results, tasks, projects and deals."
      primaryAction={
        <Button icon="plus" onClick={() => setAdding("task")}>
          New task
        </Button>
      }
      moreActions={
        <Menu
          button={({ toggle, open }) => (
            <Button type="secondary" icon="more" aria-expanded={open} onClick={toggle}>
              More
            </Button>
          )}
        >
          {(close) => (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setAdding("project");
                }}
              >
                New project
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setAdding("report");
                }}
              >
                Save a month's numbers
              </button>
              <Link to="/clients/$id/edit" params={{ id }} onClick={close}>
                Edit contact details
              </Link>
            </>
          )}
        </Menu>
      }
    >
      <div className="grid-2">
        <div className="stack">
          <ResultsCard clientId={c.id} />
          <Card title="Plan">
            {d.agreements.length ? (
              d.agreements.map((a) => (
                <Details
                  key={a.id}
                  rows={[
                    [
                      "Status",
                      <StatusLabel key="s" status={PLAN_STATE[a.state][0]}>
                        {PLAN_STATE[a.state][1]}
                      </StatusLabel>,
                    ],
                    ["Each month", money(a.monthlyCents)],
                    ["Signed", `${shortDate(a.startOn)} by ${a.signedBy}`],
                    ["First 3 months end", shortDate(a.termEndsOn)],
                    ["Next bill", a.nextBillOn ? shortDate(a.nextBillOn) : "None"],
                    ["Auto pay", a.autoPay ? "Yes, they signed for it" : "No, we send each bill"],
                    ...(a.endsOn ? ([["Last day", shortDate(a.endsOn)]] as [ReactNode, ReactNode][]) : []),
                  ]}
                />
              ))
            ) : (
              <p className="muted">No signed plan. Make a proposal from a deal.</p>
            )}
          </Card>
          <Card title="Tasks">
            <TaskList rows={d.tasks} empty="No open tasks." />
          </Card>
        </div>
        <div className="stack">
          <Card title="Account">
            <Details
              rows={[
                ["Email", c.email ?? "None"],
                ["Phone", c.phone ?? "None"],
                [
                  "Looked after by",
                  can("client.update") ? (
                    <select
                      key="o"
                      className="select"
                      aria-label="Looked after by"
                      value={c.ownerId ?? ""}
                      onChange={(e) =>
                        owner.mutate(
                          { id: c.id, ownerId: e.target.value || null },
                          { onError: (err) => toast(errText(err), { kind: "error" }) },
                        )
                      }
                    >
                      <option value="">Nobody yet</option>
                      {(team.data ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    (c.ownerName ?? "Nobody yet")
                  ),
                ],
              ]}
            />
          </Card>
          {d.linkable.length ? (
            <Card title="Our own location as a test client">
              <p className="muted small">Pick one of our own locations to show its live numbers here. Only owners can do this.</p>
              <select
                className="select"
                aria-label="Our own location"
                value={c.linkedCompanyId ?? ""}
                onChange={(e) =>
                  link.mutate(
                    { id: c.id, companyId: e.target.value || null },
                    { onError: (err) => toast(errText(err), { kind: "error" }) },
                  )
                }
              >
                <option value="">Not linked</option>
                {d.linkable.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </Card>
          ) : null}
          <Card title="Projects">
            {d.projects.length ? (
              <ul className="ag-list">
                {d.projects.map((p) => (
                  <li key={p.id}>
                    <span>{p.name}</span>
                    <StatusLabel status={PROJECT_STATUS[p.status]}>{PROJECT_STATUS_WORDS[p.status]}</StatusLabel>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No projects.</p>
            )}
          </Card>
          <Card title="Deals">
            {d.deals.length ? (
              <ul className="ag-list">
                {d.deals.map((x) => (
                  <li key={x.id}>
                    <span>{x.name}</span>
                    <span className="muted small">
                      {x.stage}
                      {x.valueCents ? ` · ${money(x.valueCents)}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No deals.</p>
            )}
          </Card>
        </div>
      </div>
      <TaskForm open={adding === "task"} onClose={() => setAdding(null)} clientId={c.id} />
      <ProjectForm open={adding === "project"} onClose={() => setAdding(null)} clientId={c.id} />
      {adding === "report" ? <ReportForm clientId={c.id} onClose={() => setAdding(null)} /> : null}
    </Page>
  );
}

function ResultsCard({ clientId }: { clientId: string }) {
  const q = useClientResults(clientId);
  if (q.error) return <LoadError error={q.error} />;
  if (!q.data) return <Skeleton rows={3} />;
  const r = q.data.results;
  const rank = rankMove(r.rankNow, r.rankBefore);
  return (
    <Card title="Results" action={<HealthPill health={r.health} />}>
      {r.noAccess ? (
        <Banner type="warning">This client shows a location you can't open, so its numbers are hidden.</Banner>
      ) : r.source === "none" ? (
        <p className="muted">No numbers yet. Use More, then Save a month's numbers.</p>
      ) : (
        <>
          <p className="muted small">
            {r.source === "linked"
              ? `Live from ${r.sourceName ?? "our location"}.`
              : "From the numbers the team saved each month."}
          </p>
          <div className="tiles">
            <Tile
              label="Leads"
              value={r.leadsNow === null ? "None" : String(r.leadsNow)}
              hint={leadsMove(r.leadsNow, r.leadsBefore, r.source === "typed")}
            />
            <Tile
              label="Cost per lead"
              value={r.costPerLeadCents === null ? "None" : money(r.costPerLeadCents)}
              hint={r.spendCents === null ? "No ad spend" : `${money(r.spendCents)} ad spend`}
            />
            <Tile
              label="Google reviews"
              value={r.reviews === null ? "None" : String(r.reviews)}
              hint={r.rating === null ? "No rating" : `${String(r.rating)} stars`}
            />
            <Tile label="Map rank" value={r.rankNow === null ? "None" : r.rankNow.toFixed(1)} hint={rank.text} />
          </div>
          {r.months.length ? <MonthBars months={r.months} /> : null}
        </>
      )}
      {q.data.reports.length ? <p className="muted small">Saved months {q.data.reports.map((x) => x.month).join(", ")}</p> : null}
    </Card>
  );
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Six months of leads as small bars, each with its number on top so it reads without an axis. */
function MonthBars({ months }: { months: ClientResults["months"] }) {
  const top = Math.max(1, ...months.map((m) => m.leads ?? 0));
  return (
    <div className="ag-bars" aria-label="Leads each month">
      {months.map((m) => (
        <div key={m.month} className="ag-bar">
          <b className="num">{m.leads ?? ""}</b>
          <span style={{ height: `${String(Math.round(((m.leads ?? 0) / top) * 100))}%` }} />
          <small className="muted">{MONTH_SHORT[Number(m.month.slice(5)) - 1]}</small>
        </div>
      ))}
    </div>
  );
}

function ReportForm({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const { company } = useSession();
  const add = useAddReport();
  const toast = useToast();
  const [month, setMonth] = useState(todayIn(company.timezone).slice(0, 7));
  const [f, setF] = useState({ leads: "", spend: "", reviews: "", rating: "", rank: "", collected: "" });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
  const cents = (v: string) => (v.trim() === "" ? undefined : Math.round(Number(v) * 100));
  return (
    <Modal
      title="Save a month's numbers"
      open
      onClose={onClose}
      footer={
        <Button
          loading={add.isPending}
          onClick={() =>
            add.mutate(
              {
                clientId,
                month,
                leads: num(f.leads),
                spendCents: cents(f.spend),
                reviews: num(f.reviews),
                rating: num(f.rating),
                rankAverage: num(f.rank),
                collectedCents: cents(f.collected),
              },
              {
                onSuccess: () => {
                  toast("Month saved");
                  onClose();
                },
                onError: (e) => toast(errText(e), { kind: "error" }),
              },
            )
          }
        >
          Save month
        </Button>
      }
    >
      <p className="muted">Leave a box empty if you don't know it. For a linked location, empty boxes take its live numbers.</p>
      <div className="form-grid">
        <InputText label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <InputText label="Leads" inputMode="numeric" value={f.leads} onChange={set("leads")} />
        <InputText label="Ad spend" prefix="$" inputMode="decimal" value={f.spend} onChange={set("spend")} />
        <InputText label="Google reviews" inputMode="numeric" value={f.reviews} onChange={set("reviews")} />
        <InputText label="Google rating" inputMode="decimal" value={f.rating} onChange={set("rating")} />
        <InputText label="Map rank average" inputMode="decimal" value={f.rank} onChange={set("rank")} description="1 is best" />
        <InputText
          label="Money their business took in"
          prefix="$"
          inputMode="decimal"
          value={f.collected}
          onChange={set("collected")}
        />
      </div>
    </Modal>
  );
}

// Results --------------------------------------------------------------------------------------------------------------

export function Results() {
  const { company } = useSession();
  const q = useResults(company.id);
  const navigate = useNavigate();
  return (
    <Page
      title="Results"
      intro="How each client is doing this month so far, against the same days last month. Green is on track, yellow needs a look, red needs help."
    >
      <DataTable<ClientResults>
        title="Clients with a plan"
        rows={q.data ?? []}
        loading={q.isLoading}
        error={q.error ? <LoadError error={q.error} /> : undefined}
        rowKey={(r) => r.clientId}
        onRowClick={(r) => void navigate({ to: "/clients/$id", params: { id: r.clientId } })}
        search={(r) => r.name}
        empty={
          <EmptyState icon="info" title="No clients with a plan yet">
            Results show once a business signs a plan.
          </EmptyState>
        }
        columns={[
          { key: "name", header: "Client", render: (r) => <b>{r.name}</b>, sort: (r) => r.name },
          { key: "health", header: "Health", render: (r) => <HealthPill health={r.health} />, sort: (r) => r.health },
          {
            key: "leads",
            header: "Leads",
            render: (r) => leadsMove(r.leadsNow, r.leadsBefore, r.source === "typed"),
            sort: (r) => r.leadsNow ?? -1,
          },
          {
            key: "cpl",
            header: "Cost per lead",
            align: "right",
            render: (r) => (r.costPerLeadCents === null ? "" : money(r.costPerLeadCents)),
            sort: (r) => r.costPerLeadCents ?? -1,
          },
          { key: "rev", header: "Reviews", align: "right", render: (r) => (r.reviews === null ? "" : String(r.reviews)) },
          { key: "rank", header: "Map rank", render: (r) => rankMove(r.rankNow, r.rankBefore).text },
          { key: "plan", header: "Each month", align: "right", render: (r) => (r.retainerCents ? money(r.retainerCents) : "") },
          { key: "next", header: "Next task", render: (r) => r.nextTask ?? "" },
          {
            key: "src",
            header: "From",
            render: (r) => (
              <span className="muted small">
                {r.source === "linked" ? (r.noAccess ? "Hidden" : "Live") : r.source === "typed" ? "Saved months" : "None"}
              </span>
            ),
          },
        ]}
      />
      <p className="muted small">
        <Icon name="info" size={16} /> Leads compare this month so far with the same days of last month, so early in a month the
        numbers are small.
      </p>
    </Page>
  );
}
